import React, { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { ArrowLeft, Check, X } from "lucide-react";
import {
  getOrderById,
  updateOrderItemStatus,
  updateOrderStatus,
} from "../../../reducers/order.slice";
import {
  convertDateFormatInUtc,
  convertDateToCT,
  formatNumberToDollar,
} from "../../../utils/util";
import { getUsers } from "../../../utils/getUser";
import { AuthContext } from "../../../context/authContext";
import AdminTable from "../../../components/shared/AdminTable";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../../../components/ui/dialog";
import { Label } from "../../../components/ui/label";
import { Textarea } from "../../../components/ui/textarea";

const statusVariant = (status) => {
  if (status === "APPROVED") return "success";
  if (status === "REJECTED") return "error";
  if (status?.includes("PENDING")) return "warning";
  return "secondary";
};

const ViewLiveOrderPage = () => {
  const [orderDetail, setOrderDetail] = useState({});
  const [showLoader, setShowLoader] = useState(false);
  const [isReferesh, setIsRefresh] = useState(false);
  const [selectedRow, setSelectedRow] = useState({});
  const [openRejectOrder, setOpenRejectOrder] = useState(false);
  const [notes, setNotes] = useState("");

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { showToast } = useContext(AuthContext);

  useEffect(() => {
    setShowLoader(true);
    dispatch(getOrderById({ id: location.state.orderId })).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setShowLoader(false);
        showToast(res?.error?.message, "error");
        return;
      }

      const newOrderItems = res?.payload?.order_items?.map((item) => ({
        ...item,
        buy_price: formatNumberToDollar(item?.buy_price),
        cashout_price: formatNumberToDollar(item?.cashout_price),
        total_amount: formatNumberToDollar(item?.total_amount),
      }));

      const createdDate =
        getUsers()?.role === "member"
          ? convertDateFormatInUtc(res?.payload?.created_at, "MMMM D, YYYY hh:mm:ss A")
          : convertDateToCT(res?.payload?.created_at, "MMMM D, YYYY hh:mm:ss A");

      setOrderDetail({
        ...res?.payload,
        newOrderItems,
        createdDate,
        orderPrice: formatNumberToDollar(res?.payload?.order_price),
      });
      setShowLoader(false);
    });
  }, [isReferesh]);

  const handleApproveOrder = (row) => {
    setShowLoader(true);
    dispatch(updateOrderItemStatus({ id: row?.id, status: "APPROVED" })).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setShowLoader(false);
        showToast(res?.error?.message, "error");
        return;
      }
      setShowLoader(false);
      setIsRefresh(!isReferesh);
      showToast("Changes saved successfully", "success");
    });
  };

  const handleOpenRejectOrder = (row) => {
    setSelectedRow(row);
    setNotes("");
    setOpenRejectOrder(true);
  };

  const handleRejectOrder = () => {
    setShowLoader(true);
    dispatch(updateOrderItemStatus({ id: selectedRow?.id, status: "REJECTED", notes })).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setShowLoader(false);
        showToast(res?.error?.message, "error");
        return;
      }
      setShowLoader(false);
      setIsRefresh(!isReferesh);
      setOpenRejectOrder(false);
      showToast("Changes saved successfully", "success");
    });
  };

  const handleUpdateStatus = () => {
    setShowLoader(true);
    const isApproved = orderDetail?.order_items?.some((item) => item?.status === "APPROVED");
    dispatch(updateOrderStatus({ id: orderDetail?.id, status: isApproved ? "APPROVED" : "REJECTED" })).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setShowLoader(false);
        showToast(res?.error?.message, "error");
        return;
      }
      setIsRefresh(!isReferesh);
      setShowLoader(false);
      showToast("Changes saved successfully", "success");
    });
  };

  const isPending = orderDetail?.order_items?.some((item) => item?.status?.includes("PENDING"));

  const columns = [
    { key: "item_name", label: "Item Name" },
    { key: "upc", label: "UPC" },
    { key: "quantity", label: "Quantity" },
    { key: "buy_price", label: "Buy Price" },
    { key: "cashout_price", label: "Cashout Price" },
    { key: "total_amount", label: "Total Amount" },
    {
      key: "status",
      label: "Status",
      render: (row) => (
        <Badge variant={statusVariant(row.status)}>
          {row.status?.replace(/_/g, " ")}
        </Badge>
      ),
    },
    {
      key: "action",
      label: "Action",
      render: (row) => {
        const isPendingItem = row?.status?.includes("PENDING");
        return (
          <div className="tw-flex tw-gap-2">
            <button
              className="tw-p-1 tw-rounded tw-border tw-border-jade tw-text-jade disabled:tw-opacity-40"
              onClick={() => handleApproveOrder(row)}
              disabled={!isPendingItem}
              title="Approve"
            >
              <Check size={16} />
            </button>
            <button
              className="tw-p-1 tw-rounded tw-border tw-border-bad tw-text-bad disabled:tw-opacity-40"
              onClick={() => handleOpenRejectOrder(row)}
              disabled={!isPendingItem}
              title="Reject"
            >
              <X size={16} />
            </button>
          </div>
        );
      },
    },
  ];

  const summaryFields = [
    { label: "Order Number", value: orderDetail?.id || "NA" },
    { label: "Member Name", value: orderDetail?.created_by_name || "NA" },
    { label: "Order Type", value: orderDetail?.type?.replace(/_/g, " ")?.toLowerCase() || "NA" },
    { label: "Order Date", value: orderDetail?.createdDate || "NA" },
  ];

  return (
    <>

      <div className="tw-max-w-5xl tw-mx-auto tw-px-4 tw-py-8">
        <button
          className="tw-flex tw-items-center tw-gap-1 tw-text-muted hover:tw-text-white tw-transition-colors tw-mb-6"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft size={16} />
          <span className="tw-text-sm">Back</span>
        </button>

        <Card>
          <CardHeader className="tw-flex tw-flex-row tw-items-center tw-justify-between">
            <CardTitle className="tw-text-xl">Order Summary</CardTitle>
            {orderDetail?.status && (
              <Badge variant={statusVariant(orderDetail.status)}>
                {orderDetail.status?.replace(/_/g, " ")}
              </Badge>
            )}
          </CardHeader>
          <CardContent className="tw-space-y-6">
            {/* Summary grid */}
            <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-x-8 tw-gap-y-3">
              {summaryFields.map(({ label, value }) => (
                <React.Fragment key={label}>
                  <span className="tw-text-muted tw-text-sm">{label}</span>
                  <span className="tw-text-white tw-text-sm tw-capitalize">{value}</span>
                </React.Fragment>
              ))}
            </div>

            {/* Items table */}
            {orderDetail?.newOrderItems?.length > 0 && (
              <AdminTable
                columns={columns}
                rows={orderDetail.newOrderItems}
                minWidth={700}
              />
            )}

            {/* Save button */}
            {orderDetail?.status?.includes("PENDING") && (
              <div className="tw-flex tw-justify-end">
                <Button onClick={handleUpdateStatus} disabled={isPending}>
                  Save
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Reject dialog */}
      <Dialog open={openRejectOrder} onOpenChange={setOpenRejectOrder}>
        <DialogContent className="tw-max-w-md">
          <DialogHeader>
            <DialogTitle>Reject Item</DialogTitle>
            <DialogDescription>
              This action will notify the member of your decision. You can provide
              a reason in the notes below.
            </DialogDescription>
          </DialogHeader>
          <div className="tw-space-y-2">
            <Label>Reason</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Provide a reason here"
              rows={4}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenRejectOrder(false)}>
              Cancel
            </Button>
            <Button
              className="tw-bg-bad hover:tw-bg-bad/80 tw-text-white"
              onClick={handleRejectOrder}
            >
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ViewLiveOrderPage;
